import React, { useState, useEffect } from 'react';

const OnboardingStep5 = ({ formData, updateFormData, onNext, onBack }) => {
    const [users, setUsers] = useState(formData.users || []);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        updateFormData({ users });
    }, [users]);

    const addUser = () => {
        setUsers([
            ...users,
            {
                id: Date.now(),
                email: '',
                full_name: '',
                role: 'viewer',
                assigned_region: '',
                send_invite: true
            }
        ]);
    };

    const removeUser = (id) => {
        setUsers(users.filter((u) => u.id !== id));
    };

    const updateUser = (id, field, value) => {
        setUsers(
            users.map((u) => (u.id === id ? { ...u, [field]: value } : u))
        );
    };

    const validate = () => {
        const newErrors = {};
        
        if (users.length === 0) {
            newErrors.general = 'Please add at least one user';
            setErrors(newErrors);
            return false;
        }

        users.forEach((user, index) => {
            if (!user.email || !user.email.includes('@')) {
                newErrors[`email_${index}`] = 'Valid email required';
            }
            if (!user.full_name || user.full_name.trim() === '') {
                newErrors[`name_${index}`] = 'Name required';
            }
            if (!user.assigned_region || user.assigned_region.trim() === '') {
                newErrors[`region_${index}`] = 'Region required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validate()) {
            onNext();
        }
    };

    const handleKeyDown = (e, currentId, currentField) => {
        if (e.key === 'Tab') {
            // Let default tab behavior handle navigation
            return;
        }
    };

    return (
        <div className="wizard-step">
            <div className="step-header">
                <h2>Add Team Members</h2>
                <p>Create user accounts for your team <span style={{ color: 'red' }}>*</span></p>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={addUser}
                    style={{
                        padding: '8px 16px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    + Add User
                </button>
            </div>

            {errors.general && (
                <div className="error-message" style={{ 
                    marginBottom: '15px',
                    padding: '15px',
                    background: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    color: '#721c24'
                }}>
                    <strong>⚠️ {errors.general}</strong>
                </div>
            )}
            
            {Object.keys(errors).length > 1 && (
                <div style={{
                    marginBottom: '15px',
                    padding: '15px',
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404'
                }}>
                    <strong>⚠️ Please fix the following errors:</strong>
                    <ul style={{ marginTop: '10px', marginBottom: '0' }}>
                        {Object.entries(errors).filter(([key]) => key !== 'general').map(([key, message]) => (
                            <li key={key}>{message}</li>
                        ))}
                    </ul>
                </div>
            )}

            {users.length > 0 && (
                <div
                    style={{
                        overflowX: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '20px'
                    }}
                >
                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                        }}
                    >
                        <thead>
                            <tr style={{ background: '#f8f9fa' }}>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', minWidth: '200px' }}>
                                    Email *
                                </th>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', minWidth: '150px' }}>
                                    Full Name *
                                </th>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', minWidth: '120px' }}>
                                    Role *
                                </th>
                                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', minWidth: '100px' }}>
                                    Region *
                                </th>
                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #ddd', minWidth: '120px' }}>
                                    Send Invite
                                </th>
                                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #ddd', width: '60px' }}>
                                    Remove
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => (
                                <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="email"
                                            value={user.email}
                                            onChange={(e) =>
                                                updateUser(user.id, 'email', e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                handleKeyDown(e, user.id, 'email')
                                            }
                                            placeholder="user@company.com"
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                border: errors[`email_${index}`] ? '2px solid #dc3545' : '1px solid #ddd',
                                                borderRadius: '4px',
                                                backgroundColor: errors[`email_${index}`] ? '#fff5f5' : 'white'
                                            }}
                                        />
                                        {errors[`email_${index}`] && (
                                            <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                                {errors[`email_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={user.full_name}
                                            onChange={(e) =>
                                                updateUser(user.id, 'full_name', e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                handleKeyDown(e, user.id, 'full_name')
                                            }
                                            placeholder="John Doe"
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                border: errors[`name_${index}`] ? '2px solid #dc3545' : '1px solid #ddd',
                                                borderRadius: '4px',
                                                backgroundColor: errors[`name_${index}`] ? '#fff5f5' : 'white'
                                            }}
                                        />
                                        {errors[`name_${index}`] && (
                                            <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                                {errors[`name_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <select
                                            value={user.role}
                                            onChange={(e) =>
                                                updateUser(user.id, 'role', e.target.value)
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <option value="viewer">Viewer</option>
                                            <option value="manager">Manager</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={user.assigned_region}
                                            onChange={(e) =>
                                                updateUser(user.id, 'assigned_region', e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                handleKeyDown(e, user.id, 'assigned_region')
                                            }
                                            placeholder="AZ"
                                            style={{
                                                width: '100%',
                                                padding: '6px',
                                                border: errors[`region_${index}`] ? '2px solid #dc3545' : '1px solid #ddd',
                                                borderRadius: '4px',
                                                backgroundColor: errors[`region_${index}`] ? '#fff5f5' : 'white'
                                            }}
                                        />
                                        {errors[`region_${index}`] && (
                                            <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                                {errors[`region_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                        <select
                                            value={user.send_invite ? 'yes' : 'no'}
                                            onChange={(e) =>
                                                updateUser(user.id, 'send_invite', e.target.value === 'yes')
                                            }
                                            style={{
                                                padding: '6px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <option value="yes">Yes</option>
                                            <option value="no">No (Later)</option>
                                        </select>
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                        <button
                                            onClick={() => removeUser(user.id)}
                                            style={{
                                                padding: '4px 8px',
                                                background: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div
                style={{
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#555'
                }}
            >
                <strong>💡 Tips:</strong>
                <ul style={{ marginTop: '10px', marginLeft: '20px' }}>
                    <li>Use Tab key to move between fields</li>
                    <li>Region should be a state abbreviation (e.g., AZ, CA, CO)</li>
                    <li>If you select "No (Later)", you can send invitations from the admin panel later</li>
                    <li>All users will receive email invitations where they can create their own passwords</li>
                </ul>
            </div>

            <div className="step-footer">
                <button onClick={onBack} className="btn-back">
                    ← Back
                </button>
                <button onClick={handleNext} className="btn-next">
                    Next →
                </button>
            </div>
        </div>
    );
};

export default OnboardingStep5;

